import { Check, Rocket, Sparkles, MessageSquare, KanbanSquare, Share2, Smartphone, Megaphone, Mail, Wallet, Lock } from "lucide-react";

const LIVE = [
  "Cobertura nacional: 18 distritos + Açores e Madeira (308 concelhos)",
  "Pesquisa por distância: só vagas até X km do teu concelho",
  "Filtros por distrito, concelho, categoria, regime e modalidade",
  "Candidaturas com estados em tempo real",
  "CV em PDF privado com controlo de acessos (RGPD)",
  "IA: gerador de anúncios, análise de CV e match score",
  "Destaques e packs pagos com Stripe",
  "Página pública de empresa e SEO (sitemap, JSON-LD)",
  "Importação automática de ofertas do IEFP em todo o país",
  "Programa de convites com prémios reais",
];

const NEXT = [
  { icon: Mail, title: "Emails e alertas reais", desc: "Avisos de novas vagas e mudanças de estado na tua caixa de correio." },
  { icon: Megaphone, title: "Espaços publicitários", desc: "Campanhas segmentadas por distrito e categoria, geridas pelo admin." },
  { icon: Wallet, title: "MB Way e Multibanco", desc: "Métodos de pagamento portugueses para além do cartão." },
  { icon: Rocket, title: "Novos setores", desc: "Além da alta rotação: escritório, tecnologia, indústria e agricultura." },
];

const FUTURE = [
  { icon: Sparkles, title: "AI matching proativo", desc: "A plataforma sugere-te vagas antes de as procurares." },
  { icon: MessageSquare, title: "Chat e entrevista vídeo", desc: "Conversa e primeira entrevista sem sair do FIRST2JOB." },
  { icon: KanbanSquare, title: "ATS Kanban", desc: "Gestão visual do funil de recrutamento para equipas." },
  { icon: Share2, title: "Multiposting", desc: "Uma vaga publicada em vários canais com um clique." },
  { icon: Smartphone, title: "Apps móveis", desc: "iOS e Android com notificações push." },
];

export const Vision = () => (
  <section id="visao" className="bg-slate-950 scroll-mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-400 mb-3">A nossa visão</p>
          <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-5xl text-white leading-[1.02]">
            Que o primeiro emprego — e o próximo — esteja a dois cliques.
          </h2>
        </div>
        <div className="text-slate-300 space-y-4 text-[15px] leading-relaxed lg:pt-4">
          <p>
            Em Portugal, os setores de maior rotação — restauração, logística, retalho, limpeza — vivem de
            anúncios dispersos, telefonemas e CVs perdidos em emails. Quem procura trabalho perde dias;
            quem contrata perde semanas.
          </p>
          <p>
            O FIRST2JOB nasce para fechar esse fosso: um só lugar credível, mobile-first e em português de
            Portugal, onde a candidatura demora dois minutos, o estado é sempre visível e a inteligência
            artificial faz o trabalho chato — escrever anúncios, ler CVs, ordenar candidatos.
          </p>
          <p className="text-slate-400">
            Construímos por fases e mostramos honestamente o que já existe e o que está por vir.
            O que está marcado como “Em breve” é visão, não promessa vendida.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-16" data-testid="roadmap">
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-7 h-full">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300">Fase 1 · Disponível hoje</span>
          </div>
          <h3 className="font-heading font-bold tracking-tighter text-xl text-white mt-4">A plataforma a funcionar</h3>
          <ul className="mt-5 flex flex-col gap-2.5">
            {LIVE.map((l) => (
              <li key={l} className="flex items-start gap-2 text-sm text-slate-300">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {l}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-7 h-full">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-300">Fase 2 · A caminho</span>
          </div>
          <h3 className="font-heading font-bold tracking-tighter text-xl text-white mt-4">Em desenvolvimento</h3>
          <div className="mt-5 flex flex-col gap-4">
            {NEXT.map((n) => (
              <div key={n.title} className="flex items-start gap-3">
                <n.icon className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" strokeWidth={1.75} />
                <div>
                  <p className="text-sm font-semibold text-white">{n.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{n.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-7 h-full" id="futuro">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-sky-400" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-sky-300">Fase 3 · Visão</span>
          </div>
          <h3 className="font-heading font-bold tracking-tighter text-xl text-white mt-4">O futuro do recrutamento</h3>
          <div className="mt-5 flex flex-col gap-3">
            {FUTURE.map((f) => (
              <div key={f.title} data-testid="em-breve-card"
                className="flex items-start gap-3 rounded-xl border border-white/5 bg-slate-900/60 px-4 py-3.5 cursor-not-allowed select-none">
                <f.icon className="w-5 h-5 text-slate-500 mt-0.5 shrink-0" strokeWidth={1.75} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-200">{f.title}</p>
                    <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] font-bold text-slate-400 border border-white/10 rounded-full px-2 py-0.5">
                      <Lock className="w-2.5 h-2.5" /> Em breve
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
